/* eslint-disable no-nested-ternary */
import * as React from 'react';
import {
  Grid, TextField, Box, Tooltip,
} from '@material-ui/core';
import type { FilterOptionsState } from '@material-ui/lab';
import { Autocomplete, Skeleton } from '@material-ui/lab';
import { matchSorter } from 'match-sorter';
import type { IRuleOutcome, POLICYCONFIGURATORSECTIONS, RuleFiltersModes } from '../Utilities/PolicyConfiguratorInterfaces';
import {
  arbitraryInputAllowedWorkflowTypes,
} from '../Utilities/PolicyConfiguratorInterfaces';
import getRuleOperations from '../../../../lib/SWRfetchers/getRuleOperations';
import getRuleOutcomeFields from '../../../../lib/SWRfetchers/getRuleOutcomeFields';
import getRuleOutcomeValues from '../../../../lib/SWRfetchers/getRuleOutcomeValues';
import type { ILabelValuePair } from '../../../marking/MarkingInterfaces';

interface IOutcomeFilterProps {
  workflowName: string;
  workflowType: POLICYCONFIGURATORSECTIONS,
  filterMode: RuleFiltersModes;
  index: number;
  outcome: IRuleOutcome;
  updateOutcome: (index: number, updatedOutcome: IRuleOutcome) => void;
  showErrors: boolean;
}

const OutcomeFilter: React.FC<IOutcomeFilterProps> = ({
  workflowType,
  workflowName,
  index,
  outcome,
  updateOutcome,
  showErrors,
}) => {
  const { outcomeFields } = getRuleOutcomeFields(workflowName, workflowType);
  const { operations } = getRuleOperations();
  const { outcomeValues } = getRuleOutcomeValues(workflowName);

  const filterOptions = (opts: ILabelValuePair[], state: FilterOptionsState<ILabelValuePair>) => matchSorter(opts, state.inputValue, {
    keys: [
      { threshold: matchSorter.rankings.WORD_STARTS_WITH, key: 'label' },
    ],
  });

  const allowArbitraryValues = arbitraryInputAllowedWorkflowTypes.includes(outcome?.field);

  return (
    <Grid container spacing={2}>
      <Grid item lg={4} md={4} xs={12}>
        <Box pl={1} py={1.5}>
          {!outcomeFields
            ? <Skeleton variant="rect" height={79} />
            : (
              <Autocomplete
                value={outcomeFields.find((field) => field.value === outcome?.field) || { label: '', value: '' }}
                onChange={(event, selectedOption) => {
                  if (selectedOption) {
                    updateOutcome(index, { ...outcome, field: selectedOption?.value });
                  } else {
                    updateOutcome(index, { field: '', operation: '', value: '' });
                  }
                }}
                autoHighlight
                autoSelect
                options={outcomeFields}
                getOptionLabel={(option) => option?.label?.toString()}
                renderInput={(params) => (
                  <TextField
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...params}
                    id={`outcome-${index}-field-select`}
                    label="Field"
                    autoFocus={index !== 0}
                    variant="outlined"
                    error={showErrors && !outcome.field}
                    helperText={showErrors && !outcome.field ? 'Select Field' : ' '}
                  />
                )}
              />
            )}
        </Box>
      </Grid>
      <Grid item lg={3} md={3} xs={12}>
        <Box py={1.5}>
          {!operations
            ? <Skeleton variant="rect" height={79} />
            : (
              <Autocomplete
                value={operations.find((field) => field.value === outcome.operation) || { label: '', value: '' }}
                onChange={(event, selectedOption) => {
                  if (selectedOption) {
                    const updatedOutcome = { ...outcome, operation: selectedOption.value, value: selectedOption?.value !== '==' ? '' : outcome.value };
                    updateOutcome(index, updatedOutcome);
                  } else {
                    updateOutcome(index, { ...outcome, operation: '' });
                  }
                }}
                autoHighlight
                autoSelect
                options={operations}
                getOptionLabel={(option) => option?.label?.toString()}
                renderInput={(params) => (
                  <TextField
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...params}
                    id={`outcome-${index}-field-select`}
                    label="Operation"
                    variant="outlined"
                    error={showErrors && !outcome?.operation}
                    helperText={showErrors && !outcome?.operation ? 'Select Operation' : ' '}
                  />
                )}
              />
            )}
        </Box>
      </Grid>
      <Grid item lg={4} md={4} xs={12}>
        <Box py={1.5}>
          {
          !outcomeValues
            ? (
              <Tooltip title="Select Field to see values">
                <TextField
                  fullWidth
                  label="Select Field to see values"
                  variant="outlined"
                  error={showErrors && !outcome?.value}
                  helperText={showErrors && !outcome?.value ? 'Select Value' : ' '}
                  disabled
                />
              </Tooltip>
            )
            : (
              <Autocomplete
                inputValue={outcome?.value}
                onInputChange={(event, selectedOption, reason) => {
                  if (reason === 'input' && selectedOption === '') {
                    updateOutcome(index, { ...outcome, value: '' });
                  } else if (outcome.field !== selectedOption && !(reason === 'reset' && selectedOption === '')) {
                    updateOutcome(index, { ...outcome, value: selectedOption });
                  }
                }}
                filterOptions={filterOptions}
                autoHighlight={!allowArbitraryValues}
                autoSelect={!allowArbitraryValues}
                freeSolo={allowArbitraryValues}
                options={allowArbitraryValues
                  ? []
                  : (
                    workflowType === 'Routing'
                      ? [...outcomeValues.filter((option) => !option.label.toLowerCase().includes('courier')), { value: '', label: '' }]
                      : [...outcomeValues, { value: '', label: '' }]
                  )}
                getOptionLabel={(option) => option?.label?.toString()}
                getOptionSelected={(option, value) => option?.value === value?.value}
                renderInput={(params) => (
                  <TextField
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...params}
                    id={`outcome-${index}-value-select`}
                    label="Value"
                    variant="outlined"
                    error={showErrors && !outcome?.value}
                    helperText={showErrors && !outcome?.value ? 'Select Value' : ' '}
                  />
                )}
              />
            )
}
        </Box>
      </Grid>
    </Grid>
  );
};

export default OutcomeFilter;
